import { Injectable, Inject } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { HttpClient, HttpHeaders, HttpRequest, HttpBackend, HttpResponse, HttpErrorResponse, HttpResponseBase, } from '@angular/common/http';
import { catchError, filter, map, Observable, of, share, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { InvalidSessionError, MissingSessionError } from "@shared/models/token-error";
import { LanguageService } from './language.service';
import { TranslateService } from '@ngx-translate/core';
import { ServiceBase } from './service-base';

@Injectable()
export class AuthService extends ServiceBase<object> {
  private accessToken$?: Observable<string>;

  constructor(
    private httpClient: HttpClient,
    private httpBackend: HttpBackend,
    @Inject("BASE_URL") baseUrl: string,
    private jwtHelper: JwtHelperService,
    private langService: LanguageService,
    private translateService: TranslateService,
  ) {
    super(httpClient, baseUrl, 'api/Account');
  }

  public get userName(): string {
    const token = this.accessToken;
    if(!token) return "";

    return this.jwtHelper.decodeToken(token)["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];
  }

  public get userLongName(): string {
    const token = this.accessToken;
    if(!token) return "";

    return this.jwtHelper.decodeToken(token)["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"];
  }

  public get userLang(): string {
    const token = this.accessToken;
    if(!token) return "";

    return this.jwtHelper.decodeToken(token)["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/country"];
  }

  public get userOrg(): string {
    const token = this.accessToken;
    if(!token) return "";

    return this.jwtHelper.decodeToken(token)["http://schemas.microsoft.com/ws/2008/06/identity/claims/groupsid"];
  }

  public get userSelectedThemeId(): number {
    const token = this.accessToken;
    if (!token) return null;

    return parseInt(this.jwtHelper.decodeToken(token)["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/webpage"]);
  }

  public get userIsDarkTheme(): boolean {
    const token = this.accessToken;
    if (!token) return null;

    return Boolean((this.jwtHelper.decodeToken(token)["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/gender"] as string).toLowerCase() === "true");
  }

  private get accessToken(): string | null {
    return localStorage.getItem(environment.accessTokenName);
  }

  private set accessToken(token: string) {
    localStorage.setItem(environment.accessTokenName, token);
  }

  private get refreshToken(): string | null {
    return localStorage.getItem(environment.refreshTokenName);
  }

  private set refreshToken(token: string) {
    localStorage.setItem(environment.refreshTokenName, token);
  }

  /**
   * Returns an observable containing the token. The request may take a while to
   * complete depending on the state of the token.
   *
   * @throws{MissingSessionError} - Throws this error if there is no user session stored. User
   * authentication handshake has not yet occurred.
   *
   * @throws{InvalidSessionError} - Throws this error if the current session isn't recognized
   * by the backend or it simply expired.
   */
  public getToken(): Observable<string> {
    const token = this.accessToken;

    if (!token) throwError(() => new MissingSessionError());
    if (this.jwtHelper.isTokenExpired(token)) return this.getNewAccessToken();
    if (this.accessToken$) return this.accessToken$;

    return of(token);
  }


  /**
   * Checks whether the use is authenticated for the given role.
   *
   * @deprecated Synchronous interface may give unreliable information
   * depending on the token state. Instead use the isAuthenticatedAsync method.
   *
   * @param role - [Optional] the role to check. If none is given, it only checks whether
   * the user is logged in.
   */
  public isAuthenticated(role?: string): boolean {
    const token = this.accessToken;
    if(!token) return false;
    if(!role) return true;

    const decodedToken = this.jwtHelper.decodeToken(token);
    const roles: Array<string> = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];

    return roles.includes(role);
  }

  /**
   * Checks whether the use is authenticated for the given role asynchronously.
   *
   * The request might take a while, because depending on the state of the token, it might need to refresh it.
   *
   * @param role - [Optional] the role to check. If none is given, it only checks whether
   * the user is logged in.
   */
  public isAuthenticatedAsync(role?: string): Observable<boolean> {
    return this.getToken()
      .pipe(
        map(token => {
          if(!role) return true;

          const decodedToken = this.jwtHelper.decodeToken(token);
          const roles: Array<string> = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];

          return roles.includes(role);
        }),
        catchError(_err => of(false))
      )
  }

  /**
   * Authenticates the user.
   *
   * @param credentials - An object containing the username and password.
   */
  public authenticate(credentials: Partial<{username: string, password: string}>): Observable<boolean> {
    return this.httpBackend.handle(
      new HttpRequest("POST", `${this.baseUrl}/login`, credentials, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      })
    ).pipe(
      filter(event => event && event.type !== 0),
      tap((response: HttpResponse<any>) => {
        this.accessToken = response.body.accessToken;
        this.refreshToken = response.body.refreshToken;
      }),
      map(_ => true),
    );
  }

  /**
   * Removes the current session of the user.
   */
  public logout(): Observable<any> {
    if (!this.accessToken) return of(0);
    return this.httpClient
      .post(`${this.baseUrl}/logout`, null, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      })
      .pipe(
        tap(
        {
          next: (_r) => {
            this.clearUserInformation();
          },
          error: (e) => {
            this.clearUserInformation();
            console.error(e);
          }
        }
        )
      );
  }

  /**
   * Clears all user information.
   */
  public clearUserInformation(): void {
    localStorage.removeItem(environment.accessTokenName);
    localStorage.removeItem(environment.refreshTokenName);
  }

  public getNewAccessToken(): Observable<string> {
    if (this.accessToken$) return this.accessToken$;

    const accessToken = this.accessToken;
    const refreshToken = this.refreshToken;

    const req: HttpRequest<any> = new HttpRequest(
      'POST',
      `${this.baseUrl}/refresh`,
      { accessToken, refreshToken },
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        }),
      }
    );

    this.accessToken$ = this.httpBackend.handle(req)
      .pipe(
        filter((res) => res instanceof HttpResponse),
        tap((res: HttpResponse<any>) => {
          const newAccessToken = res.body.accessToken;
          const newRefreshToken = res.body.refreshToken;

          this.accessToken = newAccessToken;
          this.refreshToken = newRefreshToken;
          this.accessToken$ = null;
        }),
        map(_ => this.accessToken),
        catchError(err => {
          if (err instanceof HttpErrorResponse) {
            this.accessToken$ = null;
            this.clearUserInformation();
            throw new InvalidSessionError();
          }

          throw err;
        }),
        share(),
      );

    return this.accessToken$;
  }

  public setLanguageForUser(langId: string): Observable<HttpResponseBase> {
    if (this.userLang == langId) {
      return;
    }

    return this.httpClient
      .put<HttpResponseBase>(
        `${this.baseUrl}/changelanguage`,
        { selectLanguage: langId },
        {
          observe: 'response',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      ).pipe(
        tap(_ => {
          this.langService.storeLanguageInLocalStorage(langId);
          this.translateService.resetLang(langId);
          this.translateService.use(langId);
          this.getNewAccessToken().subscribe({
            error: err => console.error(err)
          })
        }),
      );
  }

  public getUserDefLang(): Observable<any> {
    return this.httpClient.get<any>(`${this.baseUrl}/getuserlang`, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    });
  }

  public changePassword(newPassword: any): Observable<string> {
    return this.http.put<string>(
      `${this.baseUrl}/changePassword`,
      newPassword,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  }
}
